/**
 * <Skeleton> — shimmering placeholder for content-shaped loading states.
 *
 * LOADING-STATE RULE (design-system canon): use `<Skeleton>` for content-shaped
 * regions that take >200ms to load (cards, detail panels, list items); use
 * `<Spinner>` only for inline/button actions or indeterminate background ops
 * <200ms. For *tables*, reach for `<TableSkeleton>` (DataTable pattern) rather
 * than hand-building per-row `<Skeleton>`s — it renders one labelled
 * `role="status"` region with `aria-hidden` cells, avoiding chatty per-cell
 * announcements.
 *
 * UI-SPEC §Copywriting: `aria-label` is REQUIRED and must be a `NonEmptyString`
 * — typically `"Loading {entity}"` where `{entity}` is a localized noun.
 * UI-SPEC §RTL & Accessibility: "Use `<Skeleton>` for content-shaped loading
 * > 200ms; `<Spinner>` only for indeterminate background ops < 200ms."
 *
 * Respects `prefers-reduced-motion` — animation drops to a static muted fill
 * so users who disable motion still see the placeholder shape without
 * perceived pulsing.
 */
import * as React from "react";
import { cn, cva, type VariantProps } from "../../utils";
import type { NonEmptyString } from "../../types/NonEmptyString";

export const skeletonVariants = cva(
  cn(
    "bg-surface-sunken overflow-hidden",
    // Token-based shimmer sheen sweeping across the fill (motion-utilities.css).
    // A single moving highlight reads calmer than the whole-block pulse.
    // prefers-reduced-motion: the shimmer ::after is disabled in motion-utilities
    // and we drop back to the solid muted fill (no pulse, no sheen).
    "animate-shimmer",
    "motion-reduce:animate-none",
  ),
  {
    variants: {
      shape: {
        rect: "rounded-md",
        circle: "rounded-full",
      },
    },
    defaultVariants: { shape: "rect" },
  },
);

export type SkeletonProps = Omit<React.HTMLAttributes<HTMLDivElement>, "aria-label"> &
  VariantProps<typeof skeletonVariants> & {
    /** Width (CSS value) — `"100%"`, `"12rem"`, `240` (px), etc. */
    width?: number | string;
    /** Height (CSS value). */
    height?: number | string;
    /**
     * REQUIRED non-empty accessible label. Consumer supplies an entity-aware
     * message per UI-SPEC §Copywriting (`Loading employees`, `Loading payslip`).
     * Empty strings fail to typecheck via the NonEmptyString brand.
     */
    "aria-label": NonEmptyString;
  };

export const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(function Skeleton(
  { className, shape, width, height, style, "aria-label": ariaLabel, ...rest },
  ref,
) {
  const resolvedStyle: React.CSSProperties = {
    ...(width !== undefined ? { width: typeof width === "number" ? `${width}px` : width } : {}),
    ...(height !== undefined
      ? { height: typeof height === "number" ? `${height}px` : height }
      : {}),
    ...(style ?? {}),
  };

  return (
    <div
      ref={ref}
      role="status"
      aria-live="polite"
      aria-label={ariaLabel}
      aria-busy="true"
      className={cn(skeletonVariants({ shape }), className)}
      style={resolvedStyle}
      {...rest}
    />
  );
});
